import { createApi } from '@reduxjs/toolkit/query/react';
import { baseQueryWithReAuth } from "@/store/features/baseRtkQuery";

// ============ CHANNEL TYPES ============

export interface ChannelConfig {
    contextMessageCount: number;
    defaultAgent?: string;
    defaultAgentVersion?: string;
    ticketAgent?: string;
    ticketAgentVersion?: string;
    language?: string;
    contactEmail?: string;
    features: Record<string, boolean>;
}

export interface PlatformId {
    id: string;
    platform: string;
    platformId: string;
    eonChatId?: string; // Each platform connection has its own AI chat context
    createdAt: string;
}

export interface Merchant {
    id: string;
    merchantId: string;
    merchantName?: string;
    country?: string;
    createdAt: string;
}

export interface Shop {
    id: string;
    shopId: string;
    shopName?: string;
    country?: string;
    createdAt: string;
}

export interface ChannelMember {
    id: string;
    platform: string;
    platformUserId: string;
    firstName?: string;
    lastName?: string;
    contactEmail?: string;
    permissions: string[];
    isActive: boolean;
    createdAt: string;
    updatedAt: string;
}

export interface Channel {
    uid: string;  // This is the primary identifier (GUID)
    channelName: string;
    isActive: boolean;
    config: ChannelConfig;
    createdAt: string;
    updatedAt: string;
    platformIds: PlatformId[];
    merchants: Merchant[];
    shops: Shop[];
    members: ChannelMember[];
}

export interface SupportUserIdentity {
    platform: string;
    platformUserId: string;
}

export interface SupportUser {
    id: string;
    name: string;
    isActive: boolean;
    identities: SupportUserIdentity[];
    createdAt: string;
    updatedAt: string;
}

export interface DatabricksMerchant {
    merchantId: string;
    merchantName: string | null;
}

export interface DatabricksShop {
    shopId: string;
    shopName: string | null;
}

// ============ REQUEST TYPES ============

export interface CreateChannelRequest {
    channelName: string;
    platformIds: {
        platform: string;
        platformId: string;
    }[];
    shops?: {
        shopId: string;
        shopName?: string;
        country?: string;
    }[];
    merchants?: {
        merchantId: string;
        merchantName?: string;
        country?: string;
    }[];
    config?: {
        contextMessageCount?: number;
        defaultAgent?: string;
        defaultAgentVersion?: string;
        ticketAgent?: string;
        ticketAgentVersion?: string;
        language?: string;
        features?: Record<string, boolean>;
    };
}

export interface UpdateChannelRequest {
    channelName?: string;
    platformIds?: {
        platform: string;
        platformId: string;
    }[];
    shops?: {
        shopId: string;
        shopName?: string;
        country?: string;
    }[];
    merchants?: {
        merchantId: string;
        merchantName?: string;
        country?: string;
    }[];
    config?: {
        contextMessageCount?: number;
        defaultAgent?: string;
        defaultAgentVersion?: string;
        ticketAgent?: string;
        ticketAgentVersion?: string;
        language?: string;
        features?: Record<string, boolean>;
    };
}

export interface CreateMemberRequest {
    platform: string;
    platformUserId: string;
    firstName?: string;
    lastName?: string;
    contactEmail?: string;
    permissions?: string[];
}

export interface UpdateMemberRequest {
    firstName?: string;
    lastName?: string;
    contactEmail?: string;
    permissions?: string[];
    isActive?: boolean;
}

export interface GlobalContactRequest {
    globalContactEmail: string;
}

export interface GlobalPermissionsRequest {
    permission: string;
    enable: boolean;
}

export interface CreateSupportUserRequest {
    name?: string;
    firstName?: string;
    lastName?: string;
    identities: SupportUserIdentity[];
}

export interface UpdateSupportUserRequest {
    name?: string;
    identities?: SupportUserIdentity[];
}

export interface InsertMembersFromPlatformRequest {
    platform: string;
    platformChatId: string;
    members: {
        user_id: string;
        first_name?: string;
        last_name?: string;
        is_bot: boolean;
        status?: string;
    }[];
}

export interface InsertMembersResponse {
    success: boolean;
    insertedCount: number;
    updatedCount: number;
    skippedCount: number;
    message?: string;
}

// ============ CHANNEL ACTIONS (e.g. Freshdesk tickets) ============

export interface ChannelActionHistoryItem {
    id: string;
    previousStatus: string | null;
    newStatus: string;
    changedAt: string;
    changeSource: string | null;
}

/** Flexible data payload stored with the action (Freshdesk: subject, freshdesk_url, etc.) */
export interface ChannelActionData {
    subject?: string;
    platformChatId?: string;
    messageId?: string;
    freshdesk_url?: string;
    freshdesk_ticket_id?: string;
    created_by_name?: string;
    created_by_telegram_id?: string;
    channel_uid?: string;
    creation_path?: string;
    agent_name?: string;
    resolution_note?: string;
    ticket_latest_public_comment?: string;
    last_webhook_at?: string;
    title?: string;
    severity?: string;
    issue_type?: string;
    jira_key?: string;
    jira_url?: string;
    [key: string]: unknown;
}

export interface ChannelAction {
    id: string;
    channelUid: string;
    platformIdFk: string | null;
    actionType: string;
    externalId: string | null;
    status: string | null;
    data: ChannelActionData | null;
    triggeredBy: string | null;
    createdAt: string;
    updatedAt: string;
    history: ChannelActionHistoryItem[] | null;
}

export interface UpdateActionStatusRequest {
    status: string;
    changeSource?: string;
    metadata?: object;
}

export const channelsApiSlice = createApi({
    reducerPath: 'channelsApi',
    baseQuery: baseQueryWithReAuth,
    tagTypes: ['Channels', 'SupportUsers', 'Members', 'Actions'],
    endpoints: (builder) => ({
        // ============ CHANNELS ============

        // GET /api/channels - List all active channels
        getAllChannels: builder.query<Channel[], void>({
            query: () => ({
                url: 'api/channels',
                method: 'GET',
            }),
            providesTags: ['Channels'],
        }),

        // GET /api/channels/{uid} - Get channel by UID (GUID)
        getChannel: builder.query<Channel, string>({
            query: (uid) => ({
                url: `api/channels/${uid}`,
                method: 'GET',
            }),
            providesTags: (_result, _error, uid) => [{ type: 'Channels', id: uid }],
        }),

        // POST /api/channels - Create new channel
        createChannel: builder.mutation<Channel, CreateChannelRequest>({
            query: (channel) => ({
                url: 'api/channels',
                method: 'POST',
                body: channel,
            }),
            invalidatesTags: ['Channels'],
        }),

        // PUT /api/channels/{uid} - Update channel
        updateChannel: builder.mutation<Channel, { uid: string; data: UpdateChannelRequest }>({
            query: ({ uid, data }) => ({
                url: `api/channels/${uid}`,
                method: 'PUT',
                body: data,
            }),
            invalidatesTags: (_result, _error, { uid }) => [
                'Channels',
                { type: 'Channels', id: uid }
            ],
        }),

        // DELETE /api/channels/{uid} - Soft delete channel
        deleteChannel: builder.mutation<void, string>({
            query: (uid) => ({
                url: `api/channels/${uid}`,
                method: 'DELETE',
            }),
            invalidatesTags: ['Channels'],
        }),

        // ============ MEMBERS ============

        // GET /api/channels/{uid}/members - List members
        getChannelMembers: builder.query<ChannelMember[], { channelUid: string; includeInactive?: boolean }>({
            query: ({ channelUid, includeInactive = false }) => ({
                url: `api/channels/${channelUid}/members`,
                method: 'GET',
                params: { includeInactive },
            }),
            providesTags: (_result, _error, { channelUid }) => [
                { type: 'Members', id: channelUid }
            ],
        }),

        // POST /api/channels/{uid}/members - Create member
        createMember: builder.mutation<ChannelMember, { channelUid: string; data: CreateMemberRequest }>({
            query: ({ channelUid, data }) => ({
                url: `api/channels/${channelUid}/members`,
                method: 'POST',
                body: data,
            }),
            invalidatesTags: (_result, _error, { channelUid }) => [
                'Members',
                'Channels',
                { type: 'Members', id: channelUid },
                { type: 'Channels', id: channelUid }
            ],
        }),

        // PUT /api/channels/members/{memberId} - Update member
        updateMember: builder.mutation<ChannelMember, { memberId: string; data: UpdateMemberRequest }>({
            query: ({ memberId, data }) => ({
                url: `api/channels/members/${memberId}`,
                method: 'PUT',
                body: data,
            }),
            invalidatesTags: ['Members', 'Channels'],
        }),

        // DELETE /api/channels/members/{memberId} - Soft delete member
        deleteMember: builder.mutation<void, string>({
            query: (memberId) => ({
                url: `api/channels/members/${memberId}`,
                method: 'DELETE',
            }),
            invalidatesTags: ['Members', 'Channels'],
        }),

        // PUT /api/channels/{uid}/members/global-contact - Set email for all members
        updateGlobalContact: builder.mutation<{ updatedCount: number }, { channelUid: string; data: GlobalContactRequest }>({
            query: ({ channelUid, data }) => ({
                url: `api/channels/${channelUid}/members/global-contact`,
                method: 'PUT',
                body: data,
            }),
            invalidatesTags: ['Members', 'Channels'],
        }),

        // PUT /api/channels/{uid}/members/global-permission - Add/remove permission for all
        updateGlobalPermission: builder.mutation<
            { updatedCount: number; permission: string; enabled: boolean },
            { channelUid: string; data: GlobalPermissionsRequest }
        >({
            query: ({ channelUid, data }) => ({
                url: `api/channels/${channelUid}/members/global-permission`,
                method: 'PUT',
                body: data,
            }),
            invalidatesTags: ['Members', 'Channels'],
        }),

        // POST /api/channels/members/bulk - Bulk insert from platform
        insertMembersFromPlatform: builder.mutation<InsertMembersResponse, InsertMembersFromPlatformRequest>({
            query: (data) => ({
                url: 'api/channels/members/bulk',
                method: 'POST',
                body: data,
            }),
            invalidatesTags: ['Members', 'Channels'],
        }),

        // ============ DATABRICKS LOOKUPS ============

        // GET /api/channels/merchants - All merchants from Databricks
        getDatabricksMerchants: builder.query<DatabricksMerchant[], void>({
            query: () => ({
                url: 'api/channels/merchants',
                method: 'GET',
            }),
        }),

        // GET /api/channels/shops - All shops from Databricks
        getDatabricksShops: builder.query<DatabricksShop[], void>({
            query: () => ({
                url: 'api/channels/shops',
                method: 'GET',
            }),
        }),

        // ============ SUPPORT USERS ============

        // GET /api/channels/support-users - List all support users
        getAllSupportUsers: builder.query<SupportUser[], { platform?: string }>({
            query: ({ platform }) => ({
                url: 'api/channels/support-users',
                method: 'GET',
                params: platform ? { platform } : undefined,
            }),
            providesTags: ['SupportUsers'],
        }),

        // GET /api/channels/support-users/{platform}/{userId} - Check if user is support
        getSupportUser: builder.query<SupportUser, { platform: string; platformUserId: string }>({
            query: ({ platform, platformUserId }) => ({
                url: `api/channels/support-users/${platform}/${platformUserId}`,
                method: 'GET',
            }),
            providesTags: (_result, _error, { platform, platformUserId }) => [
                { type: 'SupportUsers', id: `${platform}-${platformUserId}` }
            ],
        }),

        // POST /api/channels/support-users - Add support user
        createSupportUser: builder.mutation<SupportUser, CreateSupportUserRequest>({
            query: (user) => ({
                url: 'api/channels/support-users',
                method: 'POST',
                body: user,
            }),
            invalidatesTags: ['SupportUsers'],
        }),

        // PUT /api/channels/support-users/{id} - Update by person id
        updateSupportUser: builder.mutation<
            SupportUser,
            { id: string; data: UpdateSupportUserRequest }
        >({
            query: ({ id, data }) => ({
                url: `api/channels/support-users/${id}`,
                method: 'PUT',
                body: data,
            }),
            invalidatesTags: ['SupportUsers'],
        }),

        // DELETE /api/channels/support-users/{id} - Soft delete by person id
        deleteSupportUser: builder.mutation<void, { id: string }>({
            query: ({ id }) => ({
                url: `api/channels/support-users/${id}`,
                method: 'DELETE',
            }),
            invalidatesTags: ['SupportUsers'],
        }),

        // ============ CHANNEL ACTIONS ============

        // GET /api/channels/{uid}/actions - List actions (e.g. Freshdesk tickets) for a channel
        getChannelActions: builder.query<
            ChannelAction[],
            { channelUid: string; actionType?: string; status?: string }
        >({
            query: ({ channelUid, actionType, status }) => ({
                url: `api/channels/${channelUid}/actions`,
                method: 'GET',
                params: { actionType: actionType || undefined, status: status || undefined },
            }),
            providesTags: (_result, _error, { channelUid }) => [{ type: 'Actions', id: channelUid }],
        }),

        // GET /api/channels/actions/{actionId} - Get single action with history
        getActionById: builder.query<ChannelAction, string>({
            query: (actionId) => ({
                url: `api/channels/actions/${actionId}`,
                method: 'GET',
            }),
            providesTags: (_result, _error, actionId) => [{ type: 'Actions', id: actionId }],
        }),

        // PUT /api/channels/actions/{actionId}/status - Update action status (e.g. when webhook didn't arrive)
        updateActionStatus: builder.mutation<
            ChannelAction,
            { actionId: string; data: UpdateActionStatusRequest }
        >({
            query: ({ actionId, data }) => ({
                url: `api/channels/actions/${actionId}/status`,
                method: 'PUT',
                body: data,
            }),
            invalidatesTags: (result) =>
                result
                    ? [
                          { type: 'Actions', id: result.channelUid },
                          { type: 'Actions', id: result.id },
                      ]
                    : [],
        }),
    }),
});

// Export hooks with clear naming
export const channelsApiActions = {
    useGetAllChannelsQuery: channelsApiSlice.useGetAllChannelsQuery,
    useGetChannelQuery: channelsApiSlice.useGetChannelQuery,
    useCreateChannelMutation: channelsApiSlice.useCreateChannelMutation,
    useUpdateChannelMutation: channelsApiSlice.useUpdateChannelMutation,
    useDeleteChannelMutation: channelsApiSlice.useDeleteChannelMutation,
    useGetChannelMembersQuery: channelsApiSlice.useGetChannelMembersQuery,
    useCreateMemberMutation: channelsApiSlice.useCreateMemberMutation,
    useUpdateMemberMutation: channelsApiSlice.useUpdateMemberMutation,
    useDeleteMemberMutation: channelsApiSlice.useDeleteMemberMutation,
    useUpdateGlobalContactMutation: channelsApiSlice.useUpdateGlobalContactMutation,
    useUpdateGlobalPermissionMutation: channelsApiSlice.useUpdateGlobalPermissionMutation,
    useInsertMembersFromPlatformMutation: channelsApiSlice.useInsertMembersFromPlatformMutation,
    useGetDatabricksMerchantsQuery: channelsApiSlice.useGetDatabricksMerchantsQuery,
    useGetDatabricksShopsQuery: channelsApiSlice.useGetDatabricksShopsQuery,
    useGetAllSupportUsersQuery: channelsApiSlice.useGetAllSupportUsersQuery,
    useGetSupportUserQuery: channelsApiSlice.useGetSupportUserQuery,
    useCreateSupportUserMutation: channelsApiSlice.useCreateSupportUserMutation,
    useUpdateSupportUserMutation: channelsApiSlice.useUpdateSupportUserMutation,
    useDeleteSupportUserMutation: channelsApiSlice.useDeleteSupportUserMutation,
    useGetChannelActionsQuery: channelsApiSlice.useGetChannelActionsQuery,
    useGetActionByIdQuery: channelsApiSlice.useGetActionByIdQuery,
    useUpdateActionStatusMutation: channelsApiSlice.useUpdateActionStatusMutation,
};

export default channelsApiSlice;